import MUIDialog, { DialogProps as MUIDialogProps } from '@material-ui/core/Dialog';
import IconButton from '@material-ui/core/IconButton';
import { TransitionProps } from '@material-ui/core/transitions/transition';
import IconClose from '@material-ui/icons/Close';
import React from 'react';
import { styled } from '~ui-kit/styled-components';
import { Box } from '../box/box';
import { DialogActionButtonConfig } from './dialog-action-button-config';
import { DialogActions } from './dialog-actions';
import { DialogContent } from './dialog-content';
import { DialogSubtitle } from './dialog-subtitle';
import { DialogTitle } from './dialog-title';

export interface DialogProps {
  isOpen: boolean;
  /** function triggered when the dialog is closed */
  onClose: () => void;
  title?: string;
  subtitle?: string;
  /** array of buttons to display at the bottom of the dialog */
  actions?: DialogActionButtonConfig[];
  children?: React.ReactNode;
  fullWidth?: MUIDialogProps['fullWidth'];
  maxWidth?: MUIDialogProps['maxWidth'];
  disableContentPadding?: boolean;
  disableEnforceFocus?: boolean;
  centerActionButtons?: boolean;
  /** wrap the content of the form and its action with a <form> */
  wrapInForm?: boolean;
  /** props for the <form> when using wrapInForm=true */
  formProps?: React.FormHTMLAttributes<HTMLFormElement>;
  customHeader?: React.ReactNode;
  customTitle?: React.ReactNode;
  customSubtitle?: React.ReactNode;
  TransitionProps?: TransitionProps;
}

export function Dialog({
  isOpen,
  title,
  subtitle,
  actions = [],
  onClose,
  fullWidth,
  maxWidth = 'md',
  wrapInForm = false,
  formProps,
  customHeader,
  customTitle,
  customSubtitle,
  disableContentPadding = false,
  disableEnforceFocus = false,
  centerActionButtons = false,
  TransitionProps,
  children,
}: DialogProps) {
  const content = (
    <>
      {customHeader || (
        <DialogHeader data-id="dialog-header">
          <Box flexDirection="column" flex="1 1 auto">
            {customTitle || (title && <DialogTitle>{title}</DialogTitle>)}
            {customSubtitle || (subtitle && <DialogSubtitle>{subtitle}</DialogSubtitle>)}
          </Box>
          <IconButton color="inherit" onClick={onClose}>
            <IconClose />
          </IconButton>
        </DialogHeader>
      )}

      <DialogContent $padding={disableContentPadding ? [0] : [3]}>{children}</DialogContent>

      {actions.length > 0 && (
        <DialogActions data-id="dialog-actions" actions={actions} onClose={onClose} centered={centerActionButtons} />
      )}
    </>
  );

  return (
    <DialogContainer
      disableEnforceFocus={disableEnforceFocus}
      open={isOpen}
      onClose={onClose}
      classes={{ container: 'dialog-container' }}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      TransitionProps={TransitionProps}
    >
      {wrapInForm ? <form {...formProps}>{content}</form> : content}
    </DialogContainer>
  );
}

const DialogContainer = styled(MUIDialog)`
  .dialog-container {
    font-family: ${(props) => props.theme.fontFamily.poppins};
    font-size: 16px;
  }
`;

const DialogHeader = styled(Box).attrs({ flexDirection: 'row', alignItems: 'center' })`
  background: ${(props) => props.theme.colors.primary};
  color: white;
  padding: 16px 24px;
`;
